/*
 * Copyright (c) 2024.
 * The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Column, Table} from "@tanstack/react-table";
import clsx from "clsx";
import {debounce} from "lodash";
import TableFilterDebouncedInput from "pages/Front/components/Tanstack/components/Filters/TableFilterDebouncedInput";
import TableFilterRemoveButton
    from "pages/Front/components/Tanstack/components/Filters/components/TableFilterRemoveButton";

interface TableFilterProps {
    column: Column<any, unknown>;
    table: Table<any>;
    type?: "number" | "text";
    numberInputTypeMin?: number;
    numberInputTypeMax?: number;
    className?: string;
}

const TableFilter = (props: TableFilterProps) => {
    const {t} = useTranslation();
    const [resetFilter, setResetFilter] = useState<boolean>(false);
    const numberInputTypeMin = props.numberInputTypeMin ? props.numberInputTypeMin : 0;
    const numberInputTypeMax = props.numberInputTypeMax ? props.numberInputTypeMax : 50;
    const [inputValue, setInputValue] = useState(props.column.getFilterValue());
    const classInput =
        "relative rounded inline-flex items-center px-1.5 py-1 text-base transition duration-150 border border-gray-500 hover:bg-white group-hover:bg-white placeholder:text-gray-800 focus:bg-white focus:outline focus:outline-1 focus:outline-blue-400 focus:-outline-offset-1";
    const classInputTypeNumber =
        "min-w-[50px] w-[3.6rem] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";
    const removeFiltersHandler = () => {
        const debounced = debounce(data => props.column.setFilterValue(data), 300);
        debounced(null);
        setResetFilter(true);
    };

    const customOnChangeHandler = (newValue: any) => {
        props.column.setFilterValue(newValue);
    };

    useEffect(() => {
        setInputValue(props.column.getFilterValue());
    }, [props.column.getFilterValue()]);

    return props.type === "number" ? (
        <div className={clsx("flex flex-row items-end h-full relative w-[7.7rem]", props.className && props.className)}>
            <div className="FilterTable mt-1 flex space-x-1.5">
                <TableFilterDebouncedInput
                    className={clsx(classInput, classInputTypeNumber, inputValue ? "text-blue-400 bg-white" : "text-dark-800 bg-gray-200")}
                    customOnChange={value => customOnChangeHandler((old: [number, number]) => [value, old?.[1]])}
                    max={numberInputTypeMax}
                    min={numberInputTypeMin}
                    placeholder={t("Min")}
                    resetValue={resetFilter}
                    resetValueChanged={setResetFilter}
                    type="number"
                    value={(inputValue as [number, number])?.[0] ?? ""}
                />
                <TableFilterDebouncedInput
                    className={clsx(classInput, classInputTypeNumber, inputValue ? "text-blue-400 bg-white" : "text-dark-800 bg-gray-200")}
                    customOnChange={value => customOnChangeHandler((old: [number, number]) => [old?.[0], value])}
                    max={numberInputTypeMax}
                    min={numberInputTypeMin}
                    placeholder={t("Max")}
                    resetValue={resetFilter}
                    resetValueChanged={setResetFilter}
                    type="number"
                    value={(inputValue as [number, number])?.[1] ?? ""}
                />
            </div>
            <TableFilterRemoveButton className={"mb-2"} inputValue={inputValue} onClick={removeFiltersHandler} />
        </div>
    ) : (
        <div className={"FilterTable flex flex-row items-end h-full w-36 mt-1 relative"}>
            <TableFilterDebouncedInput
                className={clsx(classInput, "w-36", inputValue ? "text-blue-400 bg-white" : "text-dark-800 bg-gray-200")}
                customOnChange={value => customOnChangeHandler(value)}
                list={props.column.id + "list"}
                placeholder={t("Search...")}
                resetValue={resetFilter}
                resetValueChanged={setResetFilter}
                type="text"
                value={(inputValue ?? "") as string}
            />
            <TableFilterRemoveButton className={"mb-2"} inputValue={inputValue} onClick={removeFiltersHandler} />
        </div>
    );
};

export default TableFilter;
